// components/RepliesPage/ConfigurationCard.tsx
import { useState } from "react";
import {
  Twitter,
  HelpCircle,
  PlusCircle,
  FilterX,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardTitle,
  CardDescription,
  CardFooter,
} from "@/components/ui/card";

interface ConfigurationCardProps {
  automatedReplies: boolean;
  onToggleAutomatedReplies: () => Promise<void>;
  onShowHelp: () => void;
  onShowProcessModal: () => void;
  onShowReplyRules: () => void;
  isLoading: boolean;
}

const ConfigurationCard: React.FC<ConfigurationCardProps> = ({
  automatedReplies,
  onToggleAutomatedReplies,
  onShowHelp,
  onShowProcessModal,
  onShowReplyRules,
  isLoading,
}) => {
  // Add state for expanded/collapsed (collapsed by default)
  const [isExpanded, setIsExpanded] = useState(false);

  // Toggle expanded/collapsed state
  const toggleExpanded = () => setIsExpanded((prev) => !prev);

  return (
    <Card className="mb-4">
      <div
        className="flex items-center justify-between cursor-pointer p-4 hover:bg-muted/10 transition-colors"
        onClick={toggleExpanded}
        aria-expanded={isExpanded}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            toggleExpanded();
            e.preventDefault();
          }
        }}
      >
        <div className="flex items-center">
          <Twitter className="h-5 w-5 mr-2" />
          <div>
            <CardTitle className="text-base sm:text-lg">
              Twitter Reply Configuration
            </CardTitle>
            <CardDescription className="mt-1">
              Configure how your Twitter replies are handled and processed
            </CardDescription>

            {/* Show a condensed summary of settings when collapsed */}
            {!isExpanded && (
              <div className="mt-1 text-sm">
                <span className="inline-flex items-center">
                  Status:
                  <span
                    className={`ml-1 font-medium ${automatedReplies ? "text-green-500" : "text-yellow-500"}`}
                  >
                    {automatedReplies ? "Automated" : "Manual approval"}
                  </span>
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation(); // Prevent card expansion toggle
              onShowHelp();
            }}
          >
            <HelpCircle className="h-4 w-4 mr-2" />
            HOW IT WORKS
          </Button>
          <div
            className="flex items-center justify-center h-8 w-8 rounded-md hover:bg-muted/20"
            aria-label={isExpanded ? "Collapse" : "Expand"}
          >
            {isExpanded ? (
              <ChevronUp className="h-5 w-5" />
            ) : (
              <ChevronDown className="h-5 w-5" />
            )}
          </div>
        </div>
      </div>

      {isExpanded && (
        <>
          <CardContent className="pb-1 pt-2 border-t">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-muted/20 rounded-lg gap-3">
              <div className="space-y-0.5">
                <Label
                  htmlFor="automated-replies"
                  className="text-base font-medium"
                >
                  Automated Replies
                </Label>
                <p className="text-sm text-muted-foreground">
                  {automatedReplies
                    ? "Replies are automatically posted without approval"
                    : "Replies require manual approval before posting"}
                </p>
              </div>
              <Switch
                id="automated-replies"
                className="color-toggle self-end sm:self-center"
                checked={automatedReplies}
                onCheckedChange={onToggleAutomatedReplies}
                disabled={isLoading}
              />
            </div>
          </CardContent>

          <CardFooter className="flex flex-col xl:flex-row justify-between border-t pt-3 pb-3 px-2">
            <div className="flex flex-col xl:flex-row w-full gap-2">
              <Button
                variant="outline"
                className="flex-1 p-4 md:p-2 h-10 text-sm uppercase"
                onClick={onShowProcessModal}
              >
                <PlusCircle className="h-4 w-4 min-w-4 mr-2" />
                <span className="">REPLY TO SPECIFIC TWEET</span>
              </Button>
              <Button
                variant="outline"
                className="flex-1 p-4 md:p-2 h-10 text-sm uppercase"
                onClick={onShowReplyRules}
              >
                <FilterX className="h-4 w-4 min-w-4 mr-2" />
                <span className="">ENABLE REPLY RULES</span>
              </Button>
            </div>
          </CardFooter>
        </>
      )}
    </Card>
  );
};

export default ConfigurationCard;
